import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X, LogOut, BarChart3, Users, Trophy, Download, Home, Palette } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useThemeStore, Theme } from '../../store/theme';

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [themeMenuOpen, setThemeMenuOpen] = useState(false);
  const { user, logout, canScout, canViewAnalytics, isAdmin } = useAuth();
  const { theme, setTheme } = useThemeStore();
  const location = useLocation();

  const navItems = [
    { path: '/', label: 'Home', icon: Home, show: true },
    { path: '/scout', label: 'Scout', icon: Trophy, show: canScout },
    { path: '/analytics', label: 'Analytics', icon: BarChart3, show: canViewAnalytics },
    { path: '/alliance', label: 'Alliance', icon: Users, show: canViewAnalytics },
    { path: '/export', label: 'Export', icon: Download, show: isAdmin },
  ];

  const activeNavItems = navItems.filter((item) => item.show);
  const isActive = (path: string) => location.pathname === path;

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="flex flex-col h-screen app-bg">
      {/* Header */}
      <header className="glass sticky top-0 z-40 border-b border-gray-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 font-bold text-xl text-white">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600" />
              StarryScout
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {activeNavItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${
                    isActive(item.path)
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-300 hover:bg-gray-800/50'
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </Link>
              ))}
            </nav>

            {/* User Info, Theme, & Logout */}
            <div className="flex items-center gap-4 relative">
              <div className="hidden sm:flex items-center gap-2 text-sm">
                <span className="text-gray-300">{user?.username}</span>
                <span className="px-2 py-1 bg-blue-600/30 text-blue-300 rounded text-xs font-medium">
                  {user?.role}
                </span>
              </div>
              {/* Theme selector */}
              <div className="relative">
                <button
                  onClick={() => setThemeMenuOpen((open) => !open)}
                  className="flex items-center gap-2 px-3 py-2 text-gray-300 hover:bg-gray-800/50 rounded-lg transition-all"
                >
                  <Palette className="w-4 h-4" />
                  <span className="hidden lg:inline text-sm capitalize">
                    {theme === 'darkBlue' ? 'Dark Blue' : theme === 'pink' ? 'Pink' : theme}
                  </span>
                </button>
                {themeMenuOpen && (
                  <div className="absolute right-0 mt-2 w-40 glass-sm py-2 text-sm z-50">
                    {[
                      { id: 'dark', label: 'Dark mode' },
                      { id: 'light', label: 'Light mode' },
                      { id: 'darkBlue', label: 'Dark blue' },
                      { id: 'pink', label: 'Pink party' },
                    ].map((opt) => (
                      <button
                        key={opt.id}
                        onClick={() => {
                          setTheme(opt.id as Theme);
                          setThemeMenuOpen(false);
                        }}
                        className={`w-full text-left px-3 py-1.5 hover:bg-gray-800/50 ${
                          theme === opt.id ? 'text-blue-400' : 'text-gray-200'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={handleLogout}
                className="md:flex items-center gap-2 px-3 py-2 text-gray-300 hover:bg-gray-800/50 rounded-lg transition-all hide"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline text-sm">Logout</span>
              </button>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="md:hidden p-2 hover:bg-gray-800/50 rounded-lg"
              >
                {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {menuOpen && (
            <motion.nav
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="md:hidden py-4 border-t border-gray-700/50 flex flex-col gap-2"
            >
              {activeNavItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMenuOpen(false)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${
                    isActive(item.path)
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-300 hover:bg-gray-800/50'
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </Link>
              ))}
            </motion.nav>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-auto pb-20 md:pb-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {children}
        </div>
      </main>
    </div>
  );
};
